import { repositories } from '../../api/Repositories';
import {ActionTree} from "vuex";
import {RootState} from "../types/RootState";
import {EventsState} from "../types/EventsState";
import {RepositoryFabric} from '@api/repositories/RepositoryFabric'
import {ObjectRepository} from '@api/repositories/ObjectRepository';
import { saveSelected, getSelected, isWelcomeTourDone, setWelcomeTourDone} from "../../helpers/selectedEventHelper";
import {QueryParametersInterface} from "@api/repositories/interfaces/QueryParametersInterface";
import {DateTime} from "luxon";
import { EventMembershipTypes } from '@api/enums/EventMembershipTypes';
import { EventMembership } from '@api/models/EventMemberShip';
import store from "../index";
import {EventMembershipRepository} from "@api/repositories/EventMembershipRepository";
import { Event, EventListFields } from "@api/models/Event";
import { ExhibitionsObserver } from "@service/store/events/exhibitions/ExhibitionsObserver";
import { RegistrationTypes } from "@api/enums/RegistrationTypes";
import { eventConverter } from "@api/models/objects/Converters";
import { chooseInitDate } from '@share/program/helpers/chooseInitDate';
import { AuthRole } from '@service/types/AuthRole';

export const actions: ActionTree<EventsState, RootState> = {
    async load({ state, commit, rootGetters, dispatch, rootState }) {
        try {
            //load all events if anon using enabled and current user is Anon
            const usingByAnon = rootGetters['services/getServiceParam']('anonymousUsing', 'isEnabled');
            const isAnon = rootState.session.roleId === AuthRole.anonymous;

            let where: any = {};

            if (!usingByAnon && !isAnon) {
                where = {
                    id: {
                        $inQuery: {
                            schema: "EventMemberships",
                            where: {
                                eventSchemaId: process.env.VUE_APP_EVENTS_SCHEMA,
                                userId: rootGetters.userId,
                                status: {
                                    $in: ['confirmed', 'mandatory']
                                },
                                isDeleted: false
                            },
                            "selectField": "eventObjectId"
                        }
                    }
                };
            }

            if (rootState.webConferenceRecordingEventId) {
                where = {
                    id: rootState.webConferenceRecordingEventId
                }
            }

            const query: QueryParametersInterface = {
                take: -1,
                include: EventListFields,
                where: where
            };

            let list = await (repositories.get(RepositoryFabric.OBJECT) as ObjectRepository).query(process.env.VUE_APP_EVENTS_SCHEMA!, query);

            list = list.map((item) => {
                if (item.beginAt) {
                    item.beginAt  = DateTime.fromISO(item.beginAt);
                }
                if (item.endAt) {
                    item.endAt  = DateTime.fromISO(item.endAt);
                }

                return item;
            })
            commit('setEvents', list);

            //@todo check if load is not callling to reload data if yes separate observer initialization
            const observer = new ExhibitionsObserver(repositories.get(RepositoryFabric.REALTIME), state.exhibitions);
            observer.connect();

            return true;
        }
        catch (e) {
            dispatch('reportException', e, {root: true});
            return false;
        }
    },
    /**
     * loading event speaker for check right and display speaker on main page
     */
    async loadEventSpeakers({ state, getters, dispatch }) {
        if (state.selected) {
            let speakersInEvents: Record<string, EventMembership[]> = {};
            try {
                speakersInEvents = await repositories.get<EventMembershipRepository>(RepositoryFabric.EVENT_MEMBERSHIP)
                    .queryParticipants(
                        process.env.VUE_APP_EVENTS_SCHEMA!,
                        [state.selected],
                        [EventMembershipTypes.speaker]
                    );
            }
            catch (e) {
                dispatch('reportError', e, {root: true});
            }

            const event = getters.selectedEvent;
            if (event) {
                // список спикеров в каждом мероприятии используется при запрете писать в чат/создавать встречи со спикерами, запрет задается параметром в мероприятии
                event.speakers = speakersInEvents[state.selected] ? speakersInEvents[state.selected].map((speaker: EventMembership) => speaker.userId) : [];

                //список профилей используется для отображение пользователей на главной странице когда нет мероприятий в компоненте EventInfo -> EventFooter
                event.speakersProfiles = speakersInEvents[state.selected] ? speakersInEvents[state.selected].map((speaker: EventMembership) => speaker) : [];
            }
        }
    },
    setSelected({commit, dispatch, state, rootGetters, rootState}, value: string) {
        saveSelected(value);
        dispatch('initWelcomeTourFlag', value);
        commit('setSelected', value);

        if (value) {
            dispatch("loadEventSpeakers");
            state.exhibitions.load(value);

            const dates = rootGetters['events/selectedEventViewParam']('dates', null);
            const date = chooseInitDate(rootGetters['events/selectedEvent'], (rootState as any).timeZoneOffset, dates);
            dispatch('program/reset', null, { root: true });

            if (!rootGetters["program/dataHelperByDate"](date)) {
                try {
                    dispatch("program/initialize", { repositories, date: date, store }, { root: true })
                }
                catch(e) {
                    dispatch('reportException', e, { root: true });
                }
            }
        }
    },
    setInitSelected({commit, state, dispatch, getters}, routeEventId) {
        let selected = getSelected() || state.selected || routeEventId || null;

        if (!selected) {
            selected = getters.sortedListByEndDateDesc?.length ? state.list[0]?.id : null;
        }

        if (selected) {
            dispatch('initWelcomeTourFlag', selected);
        }

        commit('setSelected', selected);
        dispatch("loadEventSpeakers");
        state.exhibitions.load(selected);
    },
    setSelectedIfExist({ state, dispatch }, eventId: string) {
        const result = state.list.some((event) => event.id === eventId);
        if (result) {
            dispatch('setSelected', eventId);
        }

        return result;
    },
    initWelcomeTourFlag({state, commit}, eventId: string) {
        const isDone = isWelcomeTourDone(eventId);
        const elementIndex = state.list.findIndex((item) => {
            return item.id == eventId;
        });
        if (elementIndex >= 0) {
            commit('setWelcomeTourDone', {
                index: elementIndex, value: isDone
            })
        }
    },
    setWelcomeTourFlagDone({state, commit}, eventId: string) {
        setWelcomeTourDone(eventId);
        const elementIndex = state.list.findIndex((item) => {
            return item.id == eventId;
        });
        if (elementIndex >= 0) {
            commit('setWelcomeTourDone', {
                index: elementIndex, value: true
            })
        }
    },
    updateEvent({state, commit}, event: Event) {
        const index = state.list.findIndex(item => item.id === event.id);

        if (index >= 0) {
            const newList = state.list.map(item => {
                if (item.id == event.id) {
                    return event;
                }

                return item;
            })
            commit('setEvents', newList);
        }
    },
    async registerToOpenEvent({ commit, dispatch, state }, eventId: string) {
        const objectRepository = repositories.get<ObjectRepository>(RepositoryFabric.OBJECT);
        const eventMembershipRepository = repositories.get<EventMembershipRepository>(RepositoryFabric.EVENT_MEMBERSHIP);
        try {
            const event = await objectRepository.find<Event>({
                schema: process.env.VUE_APP_EVENTS_SCHEMA!,
                objectId: eventId,
                include: EventListFields,
                converter: eventConverter
            });

            if (!event || event.registrationType != RegistrationTypes.open) {
                return false;
            }

            await eventMembershipRepository.registration(process.env.VUE_APP_EVENTS_SCHEMA!, eventId)

            commit('setEvents', [...state.list, event]);

            commit('setSelected', eventId);
            return true;
        }
        catch (e) {
            dispatch('reportException', e, {root: true});
            return false;
        }
    }
};

